import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import { Boxes, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import httpClient, { ApiResponse } from '../../api/httpClient';
import { API_ENDPOINTS } from '@/api/endpoints';
import { PaginatedResponse } from '@/api/types.ts';
import { useUrlState } from '@/hooks/shared';
import { useConfirm } from '@/hooks/useConfirm';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';

interface Instrument {
    id: string;
    symbol: string;
    base_asset_id: string;
    quote_asset_id: string;
    base_asset?: {
        id: string;
        name: string;
        symbol: string;
        type: string;
        pip_size?: string | null;
        tick_size?: string | null;
        contract_size?: string | null;
        margin_requirement?: string | null;
        is_active?: boolean | null;
        metadata?: any;
        fees?: {
            withdrawal_fee: number;
            deposit_fee: number;
        };
        limits?: {
            min_withdrawal: number;
            max_withdrawal: number;
            min_deposit: number;
            max_deposit: number;
        };
        asset_type?: {
            is_fiat: boolean;
            is_crypto: boolean;
            is_stock: boolean;
            is_commodity: boolean;
            is_index: boolean;
        };
        created_at: string;
        updated_at: string;
    };
    quote_asset?: {
        id: string;
        name: string;
        symbol: string;
        type: string;
        pip_size?: string | null;
        tick_size?: string | null;
        contract_size?: string | null;
        margin_requirement?: string | null;
        is_active?: boolean | null;
        metadata?: any;
        fees?: {
            withdrawal_fee: number;
            deposit_fee: number;
        };
        limits?: {
            min_withdrawal: number;
            max_withdrawal: number;
            min_deposit: number;
            max_deposit: number;
        };
        asset_type?: {
            is_fiat: boolean;
            is_crypto: boolean;
            is_stock: boolean;
            is_commodity: boolean;
            is_index: boolean;
        };
        created_at: string;
        updated_at: string;
    };
    tick_size: string;
    spread: string;
    lot_size: string;
    min_lot: string;
    max_lot: string;
    pip_value: string;
    leverage: number;
    max_leverage: number;
    margin_type: 'isolated' | 'cross';
    funding_model: 'swap' | 'perpetual';
    swap_rate_long: string;
    swap_rate_short: string;
    funding_rate: string;
    maker_fee: string;
    taker_fee: string;
    quote_precision: number;
    is_active: boolean;
    is_trading_enabled?: boolean;
    trading_hours?: any;
    metadata?: any;
    instrument_type?: {
        primary_type: string;
        is_forex: boolean;
        is_crypto: boolean;
        is_stock: boolean;
        is_commodity: boolean;
    };
    // PNL Calculation Fix fields
    calculation_type?: number; // 0 = Forex, 1 = CFD
    contract_size?: string | number; // e.g., 100000, 5000, 100
    spread_type?: number; // 0 = Fixed, 1 = Floating
    stop_level?: number; // Minimum distance for SL/TP in points
    gap_level?: number; // Gap level
    point_value?: string | number; // Point value for CFD calculations
    created_at: string;
    updated_at: string;
}

interface Asset {
    id: string;
    name: string;
    symbol: string;
    type: string;
}

const InstrumentsPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const confirm = useConfirm();

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'symbol', direction: 'asc' },
        pagination: { page: 1, pageSize: 25 },
    });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingInstrument, setEditingInstrument] = useState<Instrument | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);

    // Fetch instruments
    const { data: instrumentsResponse, isLoading: isInstrumentsLoading } = useQuery({
        queryKey: ['instruments', pagination.page, pagination.pageSize, filters, sort],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<Instrument>>(
                API_ENDPOINTS.CRM.INSTRUMENTS,
                {
                    params: {
                        page: pagination.page,
                        per_page: pagination.pageSize,
                        search: filters.search,
                        sort_by: sort.key,
                        sort_direction: sort.direction,
                        ...Object.fromEntries(
                            Object.entries(filters).filter(
                                ([key, value]) =>
                                    key !== 'search' &&
                                    value !== undefined &&
                                    value !== '' &&
                                    value !== null &&
                                    value !== 'all'
                            )
                        ),
                    },
                }
            );
            return data;
        },
    });

    // Fetch assets for dropdowns
    const { data: assetsResponse } = useQuery<ApiResponse<Asset[]>>({
        queryKey: ['assets'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Asset[]>>(API_ENDPOINTS.CRM.ASSETS);
            return data;
        },
    });

    const instruments = instrumentsResponse?.data || [];
    const assets = assetsResponse?.data || [];

    // Create/Update instrument mutation
    const { mutate: saveInstrument, isPending: isSaving } = useMutation({
        mutationFn: async (instrument: Partial<Instrument>) => {
            if (isNew) {
                const { data } = await httpClient.post<ApiResponse<Instrument>>(
                    API_ENDPOINTS.CRM.INSTRUMENTS,
                    instrument
                );
                return data;
            } else {
                const { data } = await httpClient.put<ApiResponse<Instrument>>(
                    `${API_ENDPOINTS.CRM.INSTRUMENTS}/${editingInstrument?.id}`,
                    instrument
                );
                return data;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instruments'] });
            enqueueSnackbar(`Instrument ${isNew ? 'created' : 'updated'} successfully`, {
                variant: 'success',
            });
            setDialogOpen(false);
            setEditingInstrument(null);
            setIsNew(false);
        },
        onError: () =>
            enqueueSnackbar(`Failed to ${isNew ? 'create' : 'update'} instrument`, {
                variant: 'error',
            }),
    });

    // Delete instrument mutation
    const { mutate: deleteInstrument, isPending: isDeleting } = useMutation({
        mutationFn: async (id: string) => {
            await httpClient.delete(`${API_ENDPOINTS.CRM.INSTRUMENTS}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instruments'] });
            enqueueSnackbar('Instrument and all related orders deleted successfully', {
                variant: 'success',
            });
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete instrument';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        },
    });

    // Toggle instrument status mutation
    const { mutate: toggleStatus, isPending: isToggling } = useMutation({
        mutationFn: async ({
            id,
            field,
            value,
        }: {
            id: string;
            field: 'is_active' | 'is_trading_enabled';
            value: boolean;
        }) => {
            const { data } = await httpClient.patch<ApiResponse<Instrument>>(
                `${API_ENDPOINTS.CRM.INSTRUMENTS}/${id}`,
                { [field]: value }
            );
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['instruments'] });
            enqueueSnackbar('Instrument status updated successfully', { variant: 'success' });
        },
        onError: () => enqueueSnackbar('Failed to update instrument status', { variant: 'error' }),
    });

    // Handle form submission
    const handleSubmit = (formData: Partial<Instrument>) => {
        saveInstrument(formData);
    };

    // Handle edit
    const handleEdit = (instrument: Instrument) => {
        setEditingInstrument(instrument);
        setIsNew(false);
        setDialogOpen(true);
    };

    // Handle add new
    const handleAdd = () => {
        setEditingInstrument(null);
        setIsNew(true);
        setDialogOpen(true);
    };

    // Handle delete
    const handleDelete = async (instrument: Instrument) => {
        const ok = await confirm({
            title: 'Delete instrument?',
            description: `"${instrument.symbol}" and all related orders and positions will be deleted. This action cannot be undone.`,
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        deleteInstrument(instrument.id);
    };

    // Handle status toggle
    const handleStatusToggle = (
        id: string,
        field: 'is_active' | 'is_trading_enabled',
        currentValue: boolean
    ) => {
        toggleStatus({ id, field, value: !currentValue });
    };

    const handleSort = (key: keyof Instrument) => {
        toggleSort(key as string);
    };

    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Define columns for the DataTable
    const columns: Column<Instrument>[] = [
        {
            id: 'symbol',
            label: 'Symbol',
            key: 'symbol',
            sortable: true,
            width: '150px',
            render: (value, instrument) => (
                <div>
                    <div className="font-medium">{value}</div>
                    <div className="text-sm text-muted-foreground">
                        {instrument.base_asset?.name} / {instrument.quote_asset?.name}
                    </div>
                </div>
            ),
        },
        {
            id: 'lot_info',
            label: 'Lot Information',
            key: 'lot_size',
            sortable: true,
            width: '150px',
            render: (_value, instrument) => (
                <dl className="space-y-0.5 text-sm">
                    <div className="flex items-center gap-1.5">
                        <dt className="text-muted-foreground">Lot</dt>
                        <dd className="tabular-figures">{instrument.lot_size}</dd>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <dt className="text-muted-foreground">Range</dt>
                        <dd className="tabular-figures">
                            {instrument.min_lot}–{instrument.max_lot}
                        </dd>
                    </div>
                </dl>
            ),
        },
        {
            id: 'spread',
            label: 'Spread',
            key: 'spread',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <div className="text-sm">
                    {instrument.spread ? (
                        <span>{parseFloat(instrument.spread).toFixed(6)}</span>
                    ) : (
                        <span className="text-muted-foreground">—</span>
                    )}
                </div>
            ),
        },
        {
            id: 'leverage',
            label: 'Leverage',
            key: 'leverage',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <div className="text-sm">
                    <div>{instrument.leverage}x</div>
                    <div className="text-muted-foreground">Max: {instrument.max_leverage}x</div>
                </div>
            ),
        },
        {
            id: 'calculation_type',
            label: 'Calculation Type',
            key: 'calculation_type',
            sortable: true,
            width: '140px',
            render: (_value, instrument) => (
                <Badge
                    className={
                        instrument.calculation_type === 1
                            ? 'border-info/20 bg-info/10 text-info'
                            : 'border-success/20 bg-success/10 text-success'
                    }
                >
                    {instrument.calculation_type === 1 ? 'CFD' : 'Forex'}
                </Badge>
            ),
        },
        {
            id: 'contract_size',
            label: 'Contract Size',
            key: 'contract_size',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <div className="text-sm">
                    {instrument.contract_size
                        ? parseFloat(String(instrument.contract_size)).toLocaleString()
                        : '—'}
                </div>
            ),
        },
        {
            id: 'spread_type',
            label: 'Spread Type',
            key: 'spread_type',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <Badge
                    className={
                        instrument.spread_type === 1
                            ? 'border-primary/20 bg-primary/10 text-primary'
                            : 'border-border bg-muted text-muted-foreground'
                    }
                >
                    {instrument.spread_type === 1 ? 'Floating' : 'Fixed'}
                </Badge>
            ),
        },
        {
            id: 'levels',
            label: 'Stop/Gap Levels',
            key: 'stop_level',
            sortable: true,
            width: '140px',
            render: (_value, instrument) => (
                <div className="text-sm">
                    <div>Stop: {instrument.stop_level ?? 0}</div>
                    <div>Gap: {instrument.gap_level ?? 0}</div>
                </div>
            ),
        },
        {
            id: 'fees',
            label: 'Fees',
            key: 'maker_fee',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <div className="text-sm">
                    <div>Maker: {(instrument.maker_fee * 100).toFixed(2)}%</div>
                    <div>Taker: {(instrument.taker_fee * 100).toFixed(2)}%</div>
                </div>
            ),
        },
        {
            id: 'funding_model',
            label: 'Funding Model',
            key: 'funding_model',
            sortable: true,
            width: '140px',
            render: (_value, instrument) => (
                <Badge className={getFundingModelColor(instrument.funding_model)}>
                    {instrument.funding_model}
                </Badge>
            ),
        },
        {
            id: 'status',
            label: 'Status',
            key: 'is_active',
            sortable: true,
            width: '120px',
            render: (_value, instrument) => (
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={instrument.is_active}
                            onCheckedChange={() =>
                                handleStatusToggle(instrument.id, 'is_active', instrument.is_active)
                            }
                            disabled={isToggling}
                        />
                        <span className="text-sm">
                            {instrument.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={instrument.is_trading_enabled}
                            onCheckedChange={() =>
                                handleStatusToggle(
                                    instrument.id,
                                    'is_trading_enabled',
                                    instrument.is_trading_enabled
                                )
                            }
                            disabled={isToggling}
                        />
                        <span className="text-sm">
                            {instrument.is_trading_enabled ? 'Trading' : 'Paused'}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            id: 'created_at',
            label: 'Created',
            key: 'created_at',
            sortable: true,
            width: '180px',
            render: value => <DateTimeFormatter date={value} className="text-sm" />,
        },
        {
            id: 'actions',
            label: 'Actions',
            key: 'id',
            sortable: false,
            width: '100px',
            render: (_value, instrument) => (
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Delete instrument"
                        title="Delete instrument"
                        onClick={event => {
                            event.stopPropagation();
                            handleDelete(instrument);
                        }}
                        disabled={isDeleting}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ];

    // Define filter fields for the GenericDataTable
    const filterFields = [
        {
            key: 'search',
            label: 'Search',
            type: 'text' as const,
            placeholder: 'Search instruments...',
        },
        {
            key: 'status',
            label: 'Status',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
            ],
        },
        {
            key: 'type',
            label: 'Type',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Types' },
                { value: 'forex', label: 'Forex' },
                { value: 'crypto', label: 'Crypto' },
                { value: 'commodity', label: 'Commodity' },
                { value: 'stock', label: 'Stock' },
            ],
        },
        {
            key: 'funding_model',
            label: 'Funding Model',
            type: 'select' as const,
            options: [
                { value: 'all', label: 'All Funding Models' },
                { value: 'swap', label: 'Swap' },
                { value: 'perpetual', label: 'Perpetual' },
            ],
        },
    ];

    // Helper functions for styling

    function getFundingModelColor(fundingModel: string) {
        switch (fundingModel) {
            case 'swap':
                return 'border-primary/20 bg-primary/10 text-primary';
            case 'perpetual':
                return 'border-warning/20 bg-warning/10 text-warning';
            default:
                return 'border-border bg-muted text-muted-foreground';
        }
    }

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Instruments"
                description="Manage trading pairs, forex, crypto, and other instruments"
                actions={
                    hasPermission(ModulePermission.INSTRUMENTS, PermissionAction.CREATE) ? (
                        <Button onClick={handleAdd}>
                            <Plus className="h-4 w-4" />
                            Add Instrument
                        </Button>
                    ) : undefined
                }
            />

            {/* Generic Data Table */}
            <GenericDataTable<Instrument>
                data={instruments}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isInstrumentsLoading}
                selectedItems={selectedInstruments}
                onSelectionChange={setSelectedInstruments}
                getItemId={instrument => instrument.id}
                sortConfig={{
                    key: sort.key as keyof Instrument,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={instrumentsResponse?.meta?.total || 0}
                totalPages={instrumentsResponse?.meta?.last_page || 1}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onRowClick={instrument => handleEdit(instrument)}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={() => {}}
                onSavePreset={() => {}}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No instruments found"
                loadingMessage="Loading instruments..."
                showExportButton={true}
                showRowClick={true}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Boxes}
                        title="No instruments found"
                        description={
                            hasActiveFilters
                                ? 'No instrument matches the current filters. Try clearing them.'
                                : 'Instruments will appear here once you add one.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : hasPermission(
                                  ModulePermission.INSTRUMENTS,
                                  PermissionAction.CREATE
                              ) ? (
                                <Button size="sm" onClick={handleAdd}>
                                    <Plus className="h-4 w-4" />
                                    Add Instrument
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Instrument Form Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isNew ? 'Add New Instrument' : 'Edit Instrument'}
                        </DialogTitle>
                        <DialogDescription>
                            Configure trading instrument parameters and settings
                        </DialogDescription>
                    </DialogHeader>

                    <InstrumentForm
                        instrument={editingInstrument}
                        assets={assets}
                        onSubmit={handleSubmit}
                        isSaving={isSaving}
                    />

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" form="instrument-form" disabled={isSaving}>
                            {isSaving
                                ? 'Saving...'
                                : isNew
                                  ? 'Create Instrument'
                                  : 'Update Instrument'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

// Instrument Form Component
interface InstrumentFormProps {
    instrument: Instrument | null;
    assets: Asset[];
    onSubmit: (data: Partial<Instrument>) => void;
    isSaving: boolean;
}

const InstrumentForm: React.FC<InstrumentFormProps> = ({
    instrument,
    assets,
    onSubmit,
    isSaving,
}) => {
    const [formData, setFormData] = useState<Partial<Instrument>>({
        symbol: '',
        base_asset_id: '',
        quote_asset_id: '',
        tick_size: '0.01',
        spread: '0.0001',
        lot_size: '1',
        min_lot: '0.01',
        max_lot: '100',
        pip_value: '10',
        leverage: 100,
        max_leverage: 500,
        margin_type: 'isolated',
        funding_model: 'swap',
        swap_rate_long: '0',
        swap_rate_short: '0',
        funding_rate: '0',
        maker_fee: '0.001',
        taker_fee: '0.002',
        quote_precision: 5,
        is_active: true,
        is_trading_enabled: true,
        // PNL Calculation Fix fields
        calculation_type: 0, // 0 = Forex, 1 = CFD
        contract_size: '100000',
        spread_type: 1, // 0 = Fixed, 1 = Floating
        stop_level: 0,
        gap_level: 0,
        point_value: '1',
    });

    // Update form data when instrument prop changes
    useEffect(() => {
        if (instrument) {
            setFormData({
                symbol: instrument.symbol || '',
                base_asset_id: instrument.base_asset.id || '',
                quote_asset_id: instrument.quote_asset.id || '',
                tick_size: instrument.tick_size || '0.01',
                spread: instrument.spread || '0.0001',
                lot_size: instrument.lot_size || '1',
                min_lot: instrument.min_lot || '0.01',
                max_lot: instrument.max_lot || '100',
                pip_value: instrument.pip_value || '10',
                leverage: instrument.leverage || 100,
                max_leverage: instrument.max_leverage || 500,
                margin_type: instrument.margin_type || 'isolated',
                funding_model: instrument.funding_model || 'swap',
                swap_rate_long: instrument.swap_rate_long || '0',
                swap_rate_short: instrument.swap_rate_short || '0',
                funding_rate: instrument.funding_rate || '0',
                maker_fee: instrument.maker_fee || '0.001',
                taker_fee: instrument.taker_fee || '0.002',
                quote_precision: instrument.quote_precision || 5,
                is_active: instrument.is_active ?? true,
                is_trading_enabled: instrument.is_trading_enabled ?? true,
                // PNL Calculation Fix fields
                calculation_type: instrument.calculation_type ?? 0,
                contract_size: instrument.contract_size
                    ? String(instrument.contract_size)
                    : '100000',
                spread_type: instrument.spread_type ?? 1,
                stop_level: instrument.stop_level ?? 0,
                gap_level: instrument.gap_level ?? 0,
                point_value: instrument.point_value ? String(instrument.point_value) : '1',
            });
        } else {
            // Reset form for new instrument
            setFormData({
                symbol: '',
                base_asset_id: '',
                quote_asset_id: '',
                tick_size: '0.01',
                spread: '0.0001',
                lot_size: '1',
                min_lot: '0.01',
                max_lot: '100',
                pip_value: '10',
                leverage: 100,
                max_leverage: 500,
                margin_type: 'isolated',
                funding_model: 'swap',
                swap_rate_long: '0',
                swap_rate_short: '0',
                funding_rate: '0',
                maker_fee: '0.001',
                taker_fee: '0.002',
                quote_precision: 5,
                is_active: true,
                is_trading_enabled: true,
                // PNL Calculation Fix fields
                calculation_type: 0,
                contract_size: '100000',
                spread_type: 1,
                stop_level: 0,
                gap_level: 0,
                point_value: '1',
            });
        }
    }, [instrument]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const handleChange = (field: keyof Instrument, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    return (
        <form id="instrument-form" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium">Basic Information</h3>

                    <div className="space-y-2">
                        <Label htmlFor="symbol">Symbol</Label>
                        <Input
                            id="symbol"
                            value={formData.symbol}
                            onChange={e => handleChange('symbol', e.target.value)}
                            placeholder="e.g., EUR/USD"
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="base_asset_id">Base Asset</Label>
                        <Select
                            value={formData.base_asset_id}
                            onValueChange={value => handleChange('base_asset_id', value)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select base asset" />
                            </SelectTrigger>
                            <SelectContent>
                                {assets.map(asset => (
                                    <SelectItem key={asset.id} value={asset.id}>
                                        {asset.symbol} - {asset.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="quote_asset_id">Quote Asset</Label>
                        <Select
                            value={formData.quote_asset_id}
                            onValueChange={value => handleChange('quote_asset_id', value)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select quote asset" />
                            </SelectTrigger>
                            <SelectContent>
                                {assets.map(asset => (
                                    <SelectItem key={asset.id} value={asset.id}>
                                        {asset.symbol} - {asset.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="tick_size">Tick Size</Label>
                        <Input
                            id="tick_size"
                            type="number"
                            step="0.00000001"
                            value={formData.tick_size}
                            onChange={e => handleChange('tick_size', parseFloat(e.target.value))}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="spread">Spread</Label>
                        <Input
                            id="spread"
                            type="number"
                            step="0.00000001"
                            value={formData.spread}
                            onChange={e => handleChange('spread', parseFloat(e.target.value))}
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Bid-ask spread (e.g., 0.0001 for 1 pip in forex)
                        </p>
                    </div>
                </div>

                {/* Trading Parameters */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium">Trading Parameters</h3>

                    <div className="space-y-2">
                        <Label htmlFor="lot_size">Lot Size</Label>
                        <Input
                            id="lot_size"
                            type="number"
                            step="0.00000001"
                            value={formData.lot_size}
                            onChange={e => handleChange('lot_size', parseFloat(e.target.value))}
                            required
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="min_lot">Min Lot</Label>
                            <Input
                                id="min_lot"
                                type="number"
                                step="0.00000001"
                                value={formData.min_lot}
                                onChange={e => handleChange('min_lot', parseFloat(e.target.value))}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="max_lot">Max Lot</Label>
                            <Input
                                id="max_lot"
                                type="number"
                                step="0.00000001"
                                value={formData.max_lot}
                                onChange={e => handleChange('max_lot', parseFloat(e.target.value))}
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="pip_value">Pip Value</Label>
                        <Input
                            id="pip_value"
                            type="number"
                            step="0.00000001"
                            value={formData.pip_value}
                            onChange={e => handleChange('pip_value', parseFloat(e.target.value))}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="calculation_type">Calculation Type</Label>
                        <Select
                            value={String(formData.calculation_type ?? 0)}
                            onValueChange={value =>
                                handleChange('calculation_type', parseInt(value))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="0">Forex</SelectItem>
                                <SelectItem value="1">CFD</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            Determines PNL calculation method
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="contract_size">Contract Size</Label>
                        <Input
                            id="contract_size"
                            type="number"
                            step="0.0001"
                            value={formData.contract_size}
                            onChange={e => handleChange('contract_size', e.target.value)}
                            placeholder="e.g., 100000 for forex, 100 for gold"
                            required
                        />
                        <p className="text-xs text-muted-foreground">
                            Standard contract size (e.g., 100000 for forex, 100 for gold, 5000 for
                            silver)
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="spread_type">Spread Type</Label>
                        <Select
                            value={String(formData.spread_type ?? 1)}
                            onValueChange={value => handleChange('spread_type', parseInt(value))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="0">Fixed</SelectItem>
                                <SelectItem value="1">Floating</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="stop_level">Stop Level</Label>
                            <Input
                                id="stop_level"
                                type="number"
                                min="0"
                                value={formData.stop_level ?? 0}
                                onChange={e =>
                                    handleChange('stop_level', parseInt(e.target.value) || 0)
                                }
                            />
                            <p className="text-xs text-muted-foreground">
                                Minimum distance for SL/TP in points
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="gap_level">Gap Level</Label>
                            <Input
                                id="gap_level"
                                type="number"
                                min="0"
                                value={formData.gap_level ?? 0}
                                onChange={e =>
                                    handleChange('gap_level', parseInt(e.target.value) || 0)
                                }
                            />
                        </div>
                    </div>

                    {formData.calculation_type === 1 && (
                        <div className="space-y-2">
                            <Label htmlFor="point_value">Point Value</Label>
                            <Input
                                id="point_value"
                                type="number"
                                step="0.00000001"
                                value={formData.point_value}
                                onChange={e => handleChange('point_value', e.target.value)}
                                required
                            />
                            <p className="text-xs text-muted-foreground">
                                Point value for CFD calculations (only for CFD instruments)
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="leverage">Default Leverage</Label>
                            <Input
                                id="leverage"
                                type="number"
                                value={formData.leverage}
                                onChange={e => handleChange('leverage', parseInt(e.target.value))}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="max_leverage">Max Leverage</Label>
                            <Input
                                id="max_leverage"
                                type="number"
                                value={formData.max_leverage}
                                onChange={e =>
                                    handleChange('max_leverage', parseInt(e.target.value))
                                }
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Fees and Funding */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium">Fees & Funding</h3>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="maker_fee">Maker Fee (%)</Label>
                            <Input
                                id="maker_fee"
                                type="number"
                                step="0.0001"
                                value={formData.maker_fee * 100}
                                onChange={e =>
                                    handleChange('maker_fee', parseFloat(e.target.value) / 100)
                                }
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="taker_fee">Taker Fee (%)</Label>
                            <Input
                                id="taker_fee"
                                type="number"
                                step="0.0001"
                                value={formData.taker_fee * 100}
                                onChange={e =>
                                    handleChange('taker_fee', parseFloat(e.target.value) / 100)
                                }
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="funding_model">Funding Model</Label>
                        <Select
                            value={formData.funding_model}
                            onValueChange={(value: 'swap' | 'perpetual') =>
                                handleChange('funding_model', value)
                            }
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="swap">Swap</SelectItem>
                                <SelectItem value="perpetual">Perpetual</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="swap_rate_long">Swap Long (%)</Label>
                            <Input
                                id="swap_rate_long"
                                type="number"
                                step="0.000001"
                                value={formData.swap_rate_long * 100}
                                onChange={e =>
                                    handleChange('swap_rate_long', parseFloat(e.target.value) / 100)
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="swap_rate_short">Swap Short (%)</Label>
                            <Input
                                id="swap_rate_short"
                                type="number"
                                step="0.000001"
                                value={formData.swap_rate_short * 100}
                                onChange={e =>
                                    handleChange(
                                        'swap_rate_short',
                                        parseFloat(e.target.value) / 100
                                    )
                                }
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="funding_rate">Funding Rate (%)</Label>
                            <Input
                                id="funding_rate"
                                type="number"
                                step="0.000001"
                                value={formData.funding_rate * 100}
                                onChange={e =>
                                    handleChange('funding_rate', parseFloat(e.target.value) / 100)
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Settings */}
                <div className="space-y-4">
                    <h3 className="text-lg font-medium">Settings</h3>

                    <div className="space-y-2">
                        <Label htmlFor="quote_precision">Quote Precision</Label>
                        <Input
                            id="quote_precision"
                            type="number"
                            min="0"
                            max="18"
                            value={formData.quote_precision}
                            onChange={e =>
                                handleChange('quote_precision', parseInt(e.target.value))
                            }
                            required
                        />
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label>Active</Label>
                                <p className="text-sm text-muted-foreground">
                                    Instrument is available in the system
                                </p>
                            </div>
                            <Switch
                                checked={formData.is_active}
                                onCheckedChange={checked => handleChange('is_active', checked)}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                                <Label>Trading Enabled</Label>
                                <p className="text-sm text-muted-foreground">
                                    Users can place trades on this instrument
                                </p>
                            </div>
                            <Switch
                                checked={formData.is_trading_enabled}
                                onCheckedChange={checked =>
                                    handleChange('is_trading_enabled', checked)
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default InstrumentsPage;
